import React from 'react';
import { cn } from '~/lib/utils';
import themes from '~/design-system/components/v1_5/Header/HeaderTheme.module.css';
import styles from './DocumentSurface.module.css';
import type { HeaderTheme } from './theme';
import { useV15Theme } from '~/design-system/components/v1_5/theme-context';

export interface DocumentSurfaceProps {
  /** The body, in the artifact's own palette. This component owns the ground and the measure, and
   *  has no opinion about anything inside it. */
  children?: React.ReactNode;
  /**
   * How the ground is filled.
   *
   * `column` is prose: one measured column down the middle of a dark ground, scrolling. `page` is a
   * website: the artifact takes the whole surface edge to edge, with no measure and no padding,
   * because a page already has its own.
   */
  variant?: 'column' | 'page';
  theme?: HeaderTheme;
  className?: string;
}

/**
 * The two kinds that are not cards.
 *
 * Every other artifact floats on the mat: a bounded object with a radius and a shadow, sitting on a
 * surface. These two do not float — they REPLACE the mat with their own ground. The caption says why
 * in six words: "a document is a document". A page of prose in a 760px letterboxed card would be a
 * screenshot of a document rather than a document, and you could not put a cursor in it.
 *
 * A WEBSITE arrives at the same place from the other direction. It used to be a card with its own
 * browser bar inside it, and a site rendered at 760px in the middle of a grey field is a picture of
 * a website — every layout it has is responding to a width nobody browses at. `page` gives it the
 * pane, and its chrome moves out to a band of the pane's own above it.
 *
 * That is also why neither has zoom: zoom is what you do to a fixed-size object you are looking at,
 * and neither of these has a fixed size to be scaled against.
 */
export function DocumentSurface({
  children,
  variant = 'column',
  theme: themeProp,
  className,
}: DocumentSurfaceProps) {
  const theme = useV15Theme(themeProp);
  return (
    <div
      className={cn(themes[theme], styles.surface, variant === 'page' && styles.page, className)}
    >
      {variant === 'page' ? children : <div className={styles.column}>{children}</div>}
    </div>
  );
}
